import { useDisplayCurrency } from '@/currency/store';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { LOCALES, LOCALE_SHORT, LOCALE_NAME, type Locale } from '@/i18n/locales';
import { useLocale } from '@/i18n/useLocale';
import { useSwitchLocale } from '@/i18n/useSwitchLocale';
import { useFormat } from '@/i18n/useFormat';
import { useSession } from '@/stores/session';
import { BADGE_MAX, selectAchievementsBadge, selectDailyBadge, selectFriendsBadge, selectMessagesBadge, selectQuestBadge, selectResume, selectTourneysBadge, useBadges } from '@/stores/badges';
import { wallet } from '@/wallet/wallet';
import { paths } from '@/app/paths';
import { Coin, Odometer, cx } from '@/ui';
import { AvatarPreset } from '@/features/profile/avatars';
import { LevelRing } from '@/features/social/Progression';
import { MiscArt } from '@/art/MiscArt';
import { useDailyBonus } from '@/features/bonus/useDailyBonus';
import { UiIcon } from '@/art/UiIcon';
import s from './shell.module.css';

/* ---------------- Logo ---------------- */
/**
 * Leyo · 乐游: the kite mark, the name in the UI language (zh: 乐游, others: Leyo) and the other script next to it,
 * muted (design/brand/leyo lockup). Latin is set in Onest, Chinese in Noto Sans SC (index.html loads the two
 * glyphs of 乐游 for every language; the full font comes with zh).
 */
export function Logo({ className }: { className?: string }) {
  const l = useLocale();
  const intl = useIntl();
  const brand = intl.formatMessage({ id: 'common.brand' });
  const han = l === 'zh';
  return (
    <Link to={paths.lobby(l)} className={cx(s.logo, className)} aria-label={brand}>
      <MiscArt name="logo" size={20} className={s.logoMark} />
      <span className={cx(s.logoText, han ? s.logoHan : s.logoLatin)}>{brand}</span>
      <span className={cx(s.logoAlt, han ? s.logoLatin : s.logoHan)} lang={han ? 'en' : 'zh'} aria-hidden="true">
        <FormattedMessage id="common.brandAlt" />
      </span>
    </Link>
  );
}

/* ---------------- Language switch ---------------- */
/** Desktop: EN DE 中文 RU segmented. Mobile: "EN ▾" with a native select (full keyboard/screen-reader support). */
export function LangSwitch({
  variant = 'segmented',
  compact,
  className,
}: {
  variant?: 'segmented' | 'dropdown';
  compact?: boolean;
  className?: string;
}) {
  const cur = useLocale();
  const switchTo = useSwitchLocale();
  const intl = useIntl();
  const label = intl.formatMessage({ id: 'common.language' });
  if (variant === 'dropdown') {
    return (
      <label className={cx(s.langDrop, className)}>
        <span aria-hidden="true">{LOCALE_SHORT[cur]} ▾</span>
        <select aria-label={label} value={cur} onChange={(e) => switchTo(e.target.value as Locale)}>
          {LOCALES.map((l) => (
            <option key={l} value={l} lang={l}>
              {LOCALE_NAME[l]}
            </option>
          ))}
        </select>
      </label>
    );
  }
  return (
    <div className={cx(s.langSeg, compact && s.langSegCompact, className)} role="group" aria-label={label}>
      {LOCALES.map((l) => (
        <button key={l} type="button" lang={l} aria-pressed={l === cur} aria-label={LOCALE_NAME[l]} onClick={() => switchTo(l)}>
          {LOCALE_SHORT[l]}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Balance ---------------- */
/** Header balance: coin + odometer. Registered as the coin-fly target of the wallet. */
export function BalanceChip({ small, decimals }: { small?: boolean; decimals?: number }) {
  const value = useSession((st) => st.displayBalance);
  const currency = useDisplayCurrency();
  const f = useFormat();
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => (ref.current ? wallet.registerTarget(ref.current) : undefined), []);
  const dec = decimals ?? (small ? 0 : 2);
  return (
    <div ref={ref} className={cx(s.balance, small && s.balanceSm, currency.enabled && s.balanceCurrency)} title={`${intl.formatMessage({ id: 'common.balance' })}${currency.enabled ? ` · ${currency.name}` : ''}`} data-fx="balance">
      <Coin size={small ? 12 : 14} />
      <Odometer value={value} format={(n) => f.tokens(n, { decimals: dec })} />
      {currency.enabled && <span data-currency-unit="" style={{ fontSize: 10 }}>{currency.ticker}</span>}
    </div>
  );
}

/* ---------------- Daily bonus button ---------------- */
/** Today's bonus already taken (known from the server): the button steps back to a quiet secondary look. */
const useDailyTaken = () => useDailyBonus((x) => x.live && !x.state.claimableNow);

export function DailyButton({ block, className }: { block?: boolean; className?: string }) {
  const open = useSession((st) => st.openDaily);
  const taken = useDailyTaken();
  return (
    <button
      type="button"
      className={cx(s.dailyBtn, block && s.dailyBtnBlock, taken && s.dailyBtnTaken, className)}
      onClick={() => open(true)}
      data-taken={taken || undefined}
    >
      <FormattedMessage id={taken ? 'common.dailyBonusTomorrow' : 'common.dailyBonus'} />
    </button>
  );
}

export function DailyPlusButton({ className }: { className?: string }) {
  const open = useSession((st) => st.openDaily);
  const taken = useDailyTaken();
  const intl = useIntl();
  return (
    <button
      type="button"
      className={cx(s.plusBtn, taken && s.plusBtnTaken, className)}
      onClick={() => open(true)}
      aria-label={intl.formatMessage({ id: taken ? 'common.dailyBonusTomorrow' : 'common.dailyBonus' })}
      data-taken={taken || undefined}
    >
      +
    </button>
  );
}

/* ---------------- Account (avatar or sign in) ---------------- */
export function AccountButton({ size = 34 }: { size?: number }) {
  const me = useSession((st) => st.me);
  const status = useSession((st) => st.status);
  const l = useLocale();
  const intl = useIntl();
  const profile = useProfileBadge();
  // guests see "Sign in" in place of the balance (WalletOrSignIn)
  if (status === 'loading' || !me) return null;
  return (
    <Link to={paths.profile(l)} className={s.avatarBtn} aria-label={profile.label ?? intl.formatMessage({ id: 'common.nav.profile' })}>
      <LevelRing size={size}>
        <AvatarPreset id={me.avatar} name={me.nickname} size={size} />
      </LevelRing>
      <NavBadge count={profile.count} onIcon className={s.avatarBadge} />
    </Link>
  );
}

/** Balance for players, "Sign in" for guests. */
export function WalletOrSignIn({ small }: { small?: boolean }) {
  const status = useSession((st) => st.status);
  const l = useLocale();
  if (status === 'user') return <BalanceChip small={small} />;
  if (status === 'loading') return null;
  return (
    <Link to={paths.signIn(l)} className={s.signIn}>
      <FormattedMessage id="common.signIn" />
    </Link>
  );
}

/* ---------------- Menu badge ---------------- */
/**
 * What needs attention on a menu item (2026-10-01): a number («99+» above 99) or, for a yes/no alert (the daily
 * bonus), a dot. Accent (teal), never red: in the zh culture red means a win. Hidden from screen readers — the link
 * gets a full `aria-label` from the `use…Badge` hooks.
 */
export function NavBadge({ count, dot, onIcon, className }: { count: number; dot?: boolean; onIcon?: boolean; className?: string }) {
  const intl = useIntl();
  if (count <= 0) {
    if (!dot) return null;
    return <span className={cx(s.navBadge, s.navBadgeDot, onIcon && s.navBadgeIcon, className)} aria-hidden="true" data-testid="nav-badge" data-dot="" />;
  }
  const text = count > BADGE_MAX ? `${intl.formatNumber(BADGE_MAX)}+` : intl.formatNumber(count);
  return (
    <span className={cx(s.navBadge, onIcon && s.navBadgeIcon, className)} aria-hidden="true" data-testid="nav-badge">
      {text}
    </span>
  );
}

export interface NavBadgeInfo {
  count: number;
  /** a yes/no alert with no number (the daily bonus): shown when count is 0 */
  dot?: boolean;
  /** the link's accessible name («Friends, 3 new friend requests») — undefined when there is nothing new */
  label: string | undefined;
}

function useNavBadge(count: number, labelId: string): NavBadgeInfo {
  const intl = useIntl();
  return { count, label: count > 0 ? intl.formatMessage({ id: labelId }, { count }) : undefined };
}

/** «Friends»: incoming friend requests */
export const useFriendsBadge = (): NavBadgeInfo => useNavBadge(useBadges(selectFriendsBadge), 'common.nav.friendsBadge');
/** «Messages»: conversations with unread messages */
export const useMessagesBadge = (): NavBadgeInfo => useNavBadge(useBadges(selectMessagesBadge), 'common.nav.messagesBadge');
/** «Tournaments»: table tourneys that need the player (seated, starts soon, an unseen prize) */
export const useTourneysBadge = (): NavBadgeInfo => useNavBadge(useBadges(selectTourneysBadge), 'common.nav.tournamentsBadge');
/** «Profile» (the avatar on desktop, the tab on the phone): achievements and moments not seen yet */
export const useProfileBadge = (): NavBadgeInfo => useNavBadge(useBadges(selectAchievementsBadge), 'common.nav.profileBadge');

/**
 * «Games»: solo games with an unfinished round — a number (the game on screen right now is not counted: the player is
 * already back in it) — or a dot when only the daily bonus or the daily quest reward is waiting.
 */
export function useGamesBadge(): NavBadgeInfo {
  const resume = useBadges(selectResume);
  const daily = useBadges(selectDailyBadge);
  const quest = useBadges(selectQuestBadge);
  const l = useLocale();
  const { pathname } = useLocation();
  const intl = useIntl();
  const count = resume.filter((g) => paths.game(l, g) !== pathname).length;
  const label =
    count > 0 || daily || quest
      ? intl.formatMessage({ id: 'common.nav.gamesBadge' }, { resume: count, daily: daily ? 'yes' : 'no', quest: quest ? 'yes' : 'no' })
      : undefined;
  return { count, dot: daily || quest, label };
}

/* ---------------- Tab bar (mobile) ---------------- */
function IcoGames() {
  return (
    <span className={s.ico} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} style={{ background: 'currentColor' }} />
      ))}
    </span>
  );
}
function IcoCup() {
  return <span className={s.ico} style={{ border: '2px solid currentColor', borderRadius: '0 0 9px 9px' }} aria-hidden="true" />;
}
function IcoFriends() {
  const c = { width: 12, height: 12, border: '2px solid currentColor', borderRadius: '50%', display: 'block' } as const;
  return (
    <span style={{ display: 'flex', height: 18, alignItems: 'center' }} aria-hidden="true">
      <span style={c} />
      <span style={{ ...c, marginLeft: -4 }} />
    </span>
  );
}
/** a speech bubble (the glyph without the raster `icons/messages`) */
function IcoMessages() {
  return (
    <svg className={s.ico} viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M3.5 2.5h11a2 2 0 0 1 2 2v6.5a2 2 0 0 1-2 2H8.5l-4 3v-3h-1a2 2 0 0 1-2-2V4.5a2 2 0 0 1 2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IcoProfile() {
  return <span className={s.ico} style={{ border: '2px solid currentColor', borderRadius: '50%' }} aria-hidden="true" />;
}

/**
 * the tab bar icons are one set: raster only when all of them read at the tab size — games / friends / profile /
 * messages through their small editions (Codex tasks 4 and 5), tournaments as is; one missing → all five keep glyphs.
 */
const TAB_ICONS = ['games', 'tournaments', 'friends', 'messages', 'profile'] as const;
const TAB_ICON_PX = 22;
const tabIcon = (name: (typeof TAB_ICONS)[number], glyph: ReactNode) => (
  <UiIcon name={name} size={TAB_ICON_PX} together={TAB_ICONS} fallback={glyph} />
);

/**
 * Phone tab bar: Games · Tournaments · Friends · Messages · Profile. Five equal columns fit 360 px: a label that does
 * not fit (DE «Nachrichten») wraps to a second line instead of pushing the bar sideways.
 */
export function TabBar() {
  const l = useLocale();
  const intl = useIntl();
  const games = useGamesBadge();
  const tourneys = useTourneysBadge();
  const friends = useFriendsBadge();
  const messages = useMessagesBadge();
  const profile = useProfileBadge();
  const tabs: Array<{ to: string; id: string; icon: ReactNode; end?: boolean; badge?: NavBadgeInfo }> = [
    { to: paths.lobby(l), id: 'common.nav.games', icon: tabIcon('games', <IcoGames />), end: true, badge: games },
    { to: paths.tournaments(l), id: 'common.nav.tournaments', icon: tabIcon('tournaments', <IcoCup />), badge: tourneys },
    { to: paths.friends(l), id: 'common.nav.friends', icon: tabIcon('friends', <IcoFriends />), badge: friends },
    { to: paths.messages(l), id: 'common.nav.messages', icon: tabIcon('messages', <IcoMessages />), badge: messages },
    { to: paths.profile(l), id: 'common.nav.profile', icon: tabIcon('profile', <IcoProfile />), badge: profile },
  ];
  return (
    <nav className={s.tabbar} aria-label={intl.formatMessage({ id: 'common.nav.label' })}>
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={s.tab} aria-label={t.badge?.label}>
          <span className={s.tabIco}>
            {t.icon}
            {t.badge && <NavBadge count={t.badge.count} dot={t.badge.dot} onIcon />}
          </span>
          <span className={s.tabLabel}>
            <FormattedMessage id={t.id} />
          </span>
        </NavLink>
      ))}
    </nav>
  );
}

/* ---------------- Desktop header nav ---------------- */
export function HeaderNav() {
  const l = useLocale();
  const isAdmin = useSession((st) => st.me?.role === 'admin');
  const intl = useIntl();
  const games = useGamesBadge();
  const tourneys = useTourneysBadge();
  const friends = useFriendsBadge();
  const messages = useMessagesBadge();
  return (
    <nav className={s.headerNav} aria-label={intl.formatMessage({ id: 'common.nav.label' })}>
      <NavLink to={paths.lobby(l)} end aria-label={games.label}>
        <FormattedMessage id="common.nav.games" />
        <NavBadge count={games.count} dot={games.dot} />
      </NavLink>
      <NavLink to={paths.tournaments(l)} aria-label={tourneys.label}>
        <FormattedMessage id="common.nav.tournaments" />
        <NavBadge count={tourneys.count} />
      </NavLink>
      <NavLink to={paths.friends(l)} aria-label={friends.label}>
        <FormattedMessage id="common.nav.friends" />
        <NavBadge count={friends.count} />
      </NavLink>
      <NavLink to={paths.messages(l)} aria-label={messages.label}>
        <FormattedMessage id="common.nav.messages" />
        <NavBadge count={messages.count} />
      </NavLink>
      {isAdmin && (
        <NavLink to={paths.admin(l)}>
          <FormattedMessage id="admin.title" />
        </NavLink>
      )}
    </nav>
  );
}

export const shellClasses = s;
